/** 
 * @file        event.js                An EventEmitter class which is API-compatible with Node's EventEmitter class.
 *
 * @author      KC Erb, kcerb@kingsds.network
 * @date        Apr 2020
 */
const { EventEmitter, sanitizeEventName } = require('./event-emitter');

/**
 * An EventEmitter which invokes event handlers in last-in first-out order.
 * If any handler returns `false` it stops invoking handlers for this event.
 * @access public
 * @memberof module:dcp/dcp-events
 * @extends module:dcp/dcp-events.EventEmitter
 */
exports.PropagatingEventEmitter = class PropagatingEventEmitter extends EventEmitter {
  constructor(debugLabel) {
    super(debugLabel)
  }

  /** Event emitter. All interfaces which emit events eventually fall through to this function. 
   *  This function invokes event handlers in the correct context for the passed eventName, as
   *  well as any catch-all (true) listeners synchronously in LIFO order. If any listener
   *  returns false, subsequent listeners are ignored.
   *
   *  @param      eventName       The name of the event to emit.
   *  @param      args            An array (not array-like) object containing the arguments 
   *                              to pass to the listener.
   */
  vemit(eventName, args) {
    eventName = sanitizeEventName(eventName)
    
    // return early if no listeners and warn on debug
    if (!this.__listeners || !(this.__listeners.hasOwnProperty(eventName) || this.__listeners.hasOwnProperty(true))) {
      if (this.debug)
        console.log("Not firing " + this.debugLabel + "::" + eventName + " -- no listeners");
      return;
    }

    if (this.debug)
      console.log("Firing " + this.debugLabel + "::" + eventName);
    
    if (this.__listeners.hasOwnProperty(eventName)) {
      let eventListenersCount = this.__listeners[eventName].length
      for (let i=eventListenersCount-1; i >= 0; i--) {
        let returnVal = this.runSoon(this, this.__listeners[eventName][i], args);
        if (returnVal === false) return 
      };
    };

    if (typeof this.__listeners[true] !== "undefined") {
      for (let i=0; i < this.__listeners[true].length; i++)
      this.runSoon(this, this.__listeners[true][i], args.concat(eventName));
    }
  }

  /**
   * Default listener goes to front of array, i.e. back of queue, doesn't trigger 'new listener'
   * event.
   */
  addDefaultListener (eventName, defaultHandler) {
    if (!this.__listeners.hasOwnProperty(eventName)) {
      this.__listeners[eventName] = [defaultHandler];
    } else {
      this.__listeners[eventName].unshift(defaultHandler);
    }
  }

  // EventEmitter uses this to determine the *how* of calling. We want to run synchronously.
  doCallback (cb) { return cb() }
}
